import { ChakraV3, FileUploadDropZone } from '@piccolohealth/ui';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { Route, Routes } from 'react-router-dom';
import { showModal } from '../../components/generic/Modal';
import { useParticipantFilesAndDocumentsFilterQuery } from '../../graphql/hooks/useParticipantQuery';
import { useParticipantFilesAndDocumentsFilter } from '../../hooks/useParticipantFilesAndDocumentsFilter';
import { ParticipantDocumentCreateModal } from '../participantDocument/ParticipantDocumentCreateModal';
import { ParticipantDocumentEditor } from '../participantDocument/ParticipantDocumentEditor';
import { ParticipantFilesAndDocumentsFilterControls } from './ParticipantFilesAndDocumentsFilterControls';
import { ParticipantFilesAndDocumentsTable } from './ParticipantFilesAndDocumentsTable';
import { ParticipantFilesAndDocumentsActionMenu } from './ParticipantFilesandDocumentsActionMenu';
import { ParticipantFileUploadModal } from './ParticipantFileUploadModal';

interface Props {
  participantId: string;
  isDisabled?: boolean;
}

export const ParticipantDocumentsContent = (props: Props) => {
  const { participantId, isDisabled } = props;

  const filter = useParticipantFilesAndDocumentsFilter();

  const { isLoading, error, filesAndDocuments, rowSelection } =
    useParticipantFilesAndDocumentsFilterQuery(participantId, filter);

  const onFilesDrag = React.useCallback(
    (files: File[]) => {
      showModal(ParticipantFileUploadModal, { participantId, files });
    },
    [participantId],
  );

  const onClickUpload = React.useCallback(() => {
    showModal(ParticipantFileUploadModal, { participantId });
  }, [participantId]);

  const onClickCreateNewDocument = () => {
    showModal(ParticipantDocumentCreateModal, { participantId });
  };

  return (
    <ChakraV3.Stack gap={4} px={6} py={4}>
      <FileUploadDropZone onFilesChange={onFilesDrag} />
      <ChakraV3.HStack w='100%'>
        <ParticipantFilesAndDocumentsFilterControls filter={filter} />
        <ChakraV3.Spacer />
        <ChakraV3.Button
          onClick={onClickCreateNewDocument}
          colorPalette='brand'
          size='sm'
          disabled={isDisabled}
        >
          <FaPlus />
          Create new document
        </ChakraV3.Button>
        <ChakraV3.Button
          onClick={onClickUpload}
          colorPalette='brand'
          size='sm'
          disabled={isDisabled}
        >
          <FaPlus />
          Upload file
        </ChakraV3.Button>
        <ParticipantFilesAndDocumentsActionMenu
          participantId={participantId}
          selectedRows={rowSelection.selectedRows}
        />
      </ChakraV3.HStack>
      <ParticipantFilesAndDocumentsTable
        isLoading={isLoading}
        isDisabled={props.isDisabled}
        refetch={() => Promise.resolve()}
        error={error}
        filesAndDocuments={filesAndDocuments}
        rowSelection={rowSelection}
      />
    </ChakraV3.Stack>
  );
};

export const ParticipantFilesAndDocuments = (props: Props) => {
  const { participantId } = props;

  return (
    <>
      <Routes>
        <Route
          path='documents/:documentId'
          element={
            <ParticipantDocumentEditor
              participantId={participantId}
              isDisabled={props.isDisabled}
            />
          }
        />
        <Route
          path='documents'
          index
          element={
            <ParticipantDocumentsContent
              participantId={participantId}
              isDisabled={props.isDisabled}
            />
          }
        />
      </Routes>
    </>
  );
};
